import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
} from "../../components/micro/input";
import Breadcrumb from "../../components/common/BreadCrumb";
import { InputError } from "../../components/error/error";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, MobileTimePicker } from "@mui/x-date-pickers";
import { Stack } from "@mui/material";
import { createOutlet } from "../../redux/companies/slice";
import ConfirmationModel from "../../components/common/ConfirmationModel";

const AddNewOutlet = ({ isEdit, onClose, outletData }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEditMode = location?.state?.mode;
  const storedData = sessionStorage.getItem("companyData");
  const companyData = storedData ? JSON.parse(storedData)?.company : null;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState(null);

  const handleEditClick = () => setIsEditing(true);

  const handleAddClick = () => {
    setShowConfirmationModal(true);
  };

  const handleSubmit = async (values) => {
    const payload = {
      is_client: 1,
      company_id: outletData?.id || companyData?.id,
      name: values.outletName,
      address: values.address,
      postal_code: values.postalCode,
      start_time: values.startTime,
      end_time: values.endTime,
      remarks: values.remarks,
    };

    const action = outletData?.id
      ? createOutlet({ id: outletData.id, ...payload })
      : createOutlet(payload);

    setIsLoading(true);
    try {
      const res = await dispatch(action);
      if (res.type === "createOutlet/fulfilled") {
        navigate(-1);
      }
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  return (
    <div
      className={`${isEdit ? "model" : ""}`}
      onClick={onClose}
      style={{ zIndex: 1300 }}
    >
      <div
        className={
          isEdit
            ? "bg-white items-center justify-center drop-shadow-lg rounded-lg w-full max-w-2xl max-h-[60vh] overflow-y-auto py-8 px-6 mx-auto relative"
            : ""
        }
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="mb-5">
          <h6 className="font-Montserrat font-normal text-xl mb-3">
            Company & Outlets
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbActive={"Add Outlet"}
            breadCrumbParent2={"Company & Outlets"}
            breadCrumbTitleKey={"/"}
          />
        </div>
        <div>
          <Formik
            initialValues={{
              outletName: outletData?.name || "",
              address: outletData?.address || "",
              postalCode: outletData?.postal_code || "",
              startTime: outletData?.start_time || "09:00:00",
              endTime: outletData?.end_time || "16:00:00",
              remarks: outletData?.remarks || "",
            }}
            validationSchema={Yup.object({
              outletName: Yup.string()
                .trim()
                .required("Please enter outlet name"),
              address: Yup.string().trim().required("Please enter address"),
              postalCode: Yup.string()
                .trim()
                .required("Please enter postal code")
                .length(6, "Postal code must be exactly 6 digits")
                .matches(/^\d+$/, "Postal code must be numeric"),
              startTime: Yup.string().required("Please enter start time"),
              endTime: Yup.string().required("Please enter end time"),
            })}
            onSubmit={(values, { setSubmitting }) => {
              setFormValues(values);
              handleAddClick();
              setSubmitting(false);
            }}
          >
            {({
              errors,
              touched,
              handleBlur,
              handleChange,
              values,
              setFieldValue,
            }) => (
              <Form>
                {/* New Outlet */}
                <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                  <h1 className="text-sm font-normal mb-2">
                    {companyData?.name}
                  </h1>
                  <h2 className="text-2xl font-normal mb-4 font-Montserrat">
                    {isEditMode === "edit" ? "Edit Outlet" : "Add New Outlet"}
                  </h2>
                  <h3 className="text-xs font-normal mb-4">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black  border hover:border-themeBtn"
                      }  ${
                        errors.outletName && touched.outletName
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName="outletName"
                      label="Outlet Name*"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.outletName}
                      errors={errors.outletName && touched.outletName}
                      errorsText={errors.outletName}
                      disabled={isEditMode == "edit" && !isEditing}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black  border hover:border-themeBtn"
                      }  ${
                        errors.postalCode && touched.postalCode
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName="postalCode"
                      label="Postal Code*"
                      type="number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.postalCode}
                      errors={errors.postalCode && touched.postalCode}
                      errorsText={errors.postalCode}
                      disabled={isEditMode == "edit" && !isEditing}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                    <InputWithLabel
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black  border hover:border-themeBtn"
                      }  ${
                        errors.address && touched.address
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      }  transition duration-300 ease-in-out`}
                      idFromName="address"
                      label="Address*"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.address}
                      errors={errors.address && touched.address}
                      errorsText={errors.address}
                      disabled={isEditMode == "edit" && !isEditing}
                    />

                    {/* Start Time */}
                    <div>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={3}>
                          <MobileTimePicker
                            label="Start Time"
                            value={
                              values.startTime
                                ? dayjs(values.startTime, "HH:mm:ss")
                                : dayjs("09:00:00", "HH:mm:ss")
                            }
                            onChange={(newValue) => {
                              const formattedTime =
                                dayjs(newValue).format("HH:mm:ss");
                              setFieldValue("startTime", formattedTime);
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  padding: "0px",
                                  minHeight: "unset",
                                  "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#F15B29",
                                      borderWidth: "1px",
                                    },
                                  },
                                },
                              },
                            }}
                            disabled={isEditMode == "edit" && !isEditing}
                          />
                          {errors.startTime && touched.startTime && (
                            <InputError errorTitle={errors.startTime} />
                          )}
                        </Stack>
                      </LocalizationProvider>
                    </div>

                    {/* End Time */}
                    <div style={{ zIndex: 0 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={3}>
                          <MobileTimePicker
                            label="End Time"
                            value={
                              values.startTime
                                ? dayjs(values.endTime, "HH:mm:ss")
                                : dayjs("16:00:00", "HH:mm:ss")
                            }
                            onChange={(newValue) => {
                              const formattedTime =
                                dayjs(newValue).format("HH:mm:ss");
                              setFieldValue("endTime", formattedTime);
                            }}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  padding: "0px",
                                  minHeight: "unset",
                                  "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#F15B29",
                                      borderWidth: "1px",
                                    },
                                  },
                                },
                              },
                            }}
                            disabled={isEditMode == "edit" && !isEditing}
                          />
                          {errors.endTime && touched.endTime && (
                            <InputError errorTitle={errors.endTime} />
                          )}
                        </Stack>
                      </LocalizationProvider>
                    </div>
                  </div>
                </div>
                {/* remark */}
                <div className="w-full bg-bgWhite shadow drop-shadow-md rounded-lg py-5 px-6 mb-4">
                  <h3 className="text-xs font-normal mb-4">
                    Remarks (Optional)
                  </h3>
                  <InputWithLabel
                    idFromName="remarks"
                    label="Description"
                    type="textarea"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.remarks}
                    errors={errors.remarks && touched.remarks}
                    errorsText={errors.remarks}
                    disabled={isEditMode == "edit" && !isEditing}
                    rows={3}
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black  border hover:border-themeBtn"
                    }  ${
                      errors.remarks && touched.remarks
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                  />
                </div>
                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value="Cancel"
                    onClick={() => {
                      if (isEdit) {
                        onClose();
                      } else {
                        navigate(-1);
                      }
                    }}
                  />
                  {isEditMode === "edit" ? (
                    isEditing ? (
                      <>
                        <DefaultButton type="submit" value="Save" />
                      </>
                    ) : (
                      <DefaultButton
                        value="Edit"
                        onClick={handleEditClick}
                        type="submit"
                      />
                    )
                  ) : (
                    <DefaultButton type="submit" value="Save" />
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleSubmit(formValues)}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default AddNewOutlet;
